// @ts-nocheck
import * as React from "react";
import { Logo, Ic, Sidebar, Topbar, LineChart, BarChart, Kpi, AppFooter, useCurrentUser, apiErrorMessage } from "./common";
import { usePermissionsFromSession } from "../components/permissions";
import { Pager } from "../components/Pager";

// 한 페이지 건수 — 우측 상세 패널과 함께 보이는 표라서 큰 값은 부담.
const PER_PAGE = 20;

const KIND_LABEL = {
  signup: "회원 가입",
  qualification: "자격증 갱신",
  org_contract: "기관 협약",
  manager_license: "매니저 자격 등록",
  care_application: "케어 신청",
  org_transfer: "소속 기관 변경",
  case_transfer: "케이스 이관",
  org_contact_transfer: "기관 담당자 이관",
};
const KIND_COLOR = {
  signup: "sky",
  qualification: "orange",
  org_contract: "green",
  manager_license: "amber",
  care_application: "rose",
  org_transfer: "violet",
  case_transfer: "sky",
  org_contact_transfer: "violet",
};

function Approval() {
  const { user, loaded: userLoaded } = useCurrentUser();
  const perms = usePermissionsFromSession({ user, loaded: userLoaded });
  const [data, setData] = React.useState(null);
  const [tab, setTab] = React.useState(null);   // null = all pending; or specific kind
  const [selectedId, setSelectedId] = React.useState(null);
  const [reviewNote, setReviewNote] = React.useState("");
  const [showResolved, setShowResolved] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [actionErr, setActionErr] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [q, setQ] = React.useState("");

  const load = React.useCallback(async () => {
    const params = new URLSearchParams();
    // "완료/거부" tab needs both approved + rejected. The backend accepts a
    // comma list so we get the right view in one request — sending an
    // empty status before would silently leak pending rows into the view.
    params.set("status", showResolved ? "approved,rejected" : "pending");
    if (tab) params.set("kind", tab);
    if (q)   params.set("q", q);
    params.set("page", String(page));
    params.set("per_page", String(PER_PAGE));
    try {
      const r = await fetch(`/api/v1/approvals?${params.toString()}`, { credentials: "same-origin" });
      // require_approver! returns 401/403 for non-operator roles; without
      // this guard the access to j.approvals.length below would throw and
      // leave the page stuck at the "불러오는 중…" placeholder.
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setData({ _denied: r.status, _message: apiErrorMessage(j, r.status), approvals: [], counts: {}, pagination: {} });
        return;
      }
      const j = await r.json();
      setData(j);
      if (j.approvals?.length > 0 && !selectedId) setSelectedId(j.approvals[0].id);
    } catch (e) {
      setData({ _denied: 0, _message: apiErrorMessage(e), approvals: [], counts: {}, pagination: {} });
    }
  }, [tab, showResolved, page, q]);

  React.useEffect(() => { load(); }, [load]);

  // 탭/검색/필터 변경 시 1페이지로 자동 복귀(빈 페이지 방지). selectedId도 초기화해
  // 현재 페이지 첫 항목이 자동 선택되게 한다.
  React.useEffect(() => { setPage(1); setSelectedId(null); }, [tab, showResolved, q]);
  // 선택한 신청자가 바뀌면 작성 중이던 검토 메모를 초기화한다. 없으면 직전 신청자에
  // 쓰던 메모가 다른 신청자 화면에 그대로 남아 보인다(제출 성공 시에만 비워졌으므로).
  React.useEffect(() => { setReviewNote(""); }, [selectedId]);
  // 페이지 범위 초과(필터로 결과가 줄어든 경우) 보정.
  React.useEffect(() => {
    const totalPages = data?.pagination?.total_pages ?? 1;
    if (page > totalPages && totalPages > 0) setPage(1);
  }, [page, data?.pagination?.total_pages]);

  if (!data) return <div className="app muted" style={{padding:40}}>불러오는 중…</div>;
  if (data._denied) return (
    <div className="app" style={{height:'100vh'}}>
      <div className="shell">
        <Sidebar role="region" active="approval"/>
        <div style={{display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <Topbar title="승인 관리"/>
          <div className="main">
            <div className="card" style={{padding:'60px 40px', textAlign:'center', marginTop:20}}>
              <div className="h2" style={{fontSize:18, marginBottom:8}}>
                {data._denied === 401 ? '로그인이 필요합니다' :
                 data._denied === 403 ? '접근 권한이 없습니다' : '승인 관리를 불러올 수 없습니다'}
              </div>
              <div className="muted" style={{fontSize:13}}>{data._message}</div>
              <div className="muted" style={{fontSize:11.5, marginTop:14}}>
                승인 관리는 권역 운영진 / 시스템 관리자만 접근할 수 있습니다.
              </div>
            </div>
          </div>
          <AppFooter/>
        </div>
      </div>
    </div>
  );

  const visible = data.approvals;
  const selected = visible.find(a => a.id === selectedId) || visible[0];

  // Shared writer for approve + reject — same error path, same loading
  // guard, same audit-note payload.
  const submitReview = async (action, target = selected) => {
    if (!target || submitting) return;
    setActionErr(null); setSubmitting(true);
    try {
      const r = await fetch(`/api/v1/approvals/${target.id}/${action}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ note: reviewNote }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (r.status === 401)      setActionErr("로그인이 필요합니다.");
        else if (r.status === 403) setActionErr(j.message || "승인 권한이 없습니다.");
        else if (r.status === 422) setActionErr(apiErrorMessage(j, r.status));
        else                       setActionErr(apiErrorMessage(j, r.status));
        // 이미 처리된 건이면 목록을 새로 받아 상태 동기화 (다른 운영자가 먼저 처리한 케이스).
        if (r.status === 422) await load();
        return;
      }
      setReviewNote("");
      setSelectedId(null);
      // 사이드바의 "승인 관리" 배지 카운트를 즉시 갱신.
      // sessions/me를 재요청해 nav_counts를 새로 가져온다.
      window.dispatchEvent(new Event("session:refresh"));
      await load();
    } catch (e) { setActionErr(apiErrorMessage(e)); } finally { setSubmitting(false); }
  };
  // 사용자 의도 확인 — 잘못 클릭한 처리는 되돌릴 수 없음(상태 가드로 재처리 차단).
  // 메모 미입력 시 한 줄 경고를 같이 표시해 감사 무결성 인식.
  const confirmDecide = (action, target) => {
    const label = action === "approve" ? "승인" : "반려";
    const noteLine = reviewNote.trim()
      ? `메모: "${reviewNote.trim()}"`
      : "⚠ 메모 없이 처리됩니다. 감사 로그에 사유가 남지 않습니다.";
    return confirm(
      `[${target.title}] 건을 ${label} 처리합니다.\n` +
      `신청자: ${target.requester_name || '-'}\n\n` +
      noteLine + "\n\n진행하시겠습니까?"
    );
  };
  const doApprove = (target) => { if (confirmDecide("approve", target)) submitReview("approve", target); };
  const doReject  = (target) => { if (confirmDecide("reject",  target)) submitReview("reject",  target); };

  const tabs = [
    { l: "전체 대기", k: null, n: data.counts.pending },
    { l: KIND_LABEL.signup,           k: "signup",           n: data.counts.by_kind?.signup || 0 },
    { l: KIND_LABEL.qualification,    k: "qualification",    n: data.counts.by_kind?.qualification || 0 },
    { l: KIND_LABEL.org_contract,     k: "org_contract",     n: data.counts.by_kind?.org_contract || 0 },
    { l: KIND_LABEL.manager_license,  k: "manager_license",  n: data.counts.by_kind?.manager_license || 0 },
    { l: KIND_LABEL.care_application, k: "care_application", n: data.counts.by_kind?.care_application || 0 },
  ];

  return (
    <div className="app" style={{height:'100vh'}}>
      <div className="shell">
        <Sidebar role="region" active="approval"/>
        <div style={{display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <Topbar title="승인 관리" sub={`대기 ${data.counts.pending.toLocaleString()} · 처리완료 ${(data.counts.approved + data.counts.rejected).toLocaleString()} · 행 선택 후 우측에서 메모와 함께 처리`}/>
          <div className="main">

            <div style={{display:'flex', gap:0, borderBottom:'1px solid var(--border-soft)', marginBottom:18}}>
              {tabs.map((t,i)=>{
                const active = (tab === t.k) && !showResolved;
                return (
                  <div key={i} onClick={()=>{ setTab(t.k); setShowResolved(false); setSelectedId(null); }}
                    style={{padding:'12px 18px', fontSize:13, fontWeight: active?700:500, color: active?'var(--orange-700)':'var(--ink-2)', borderBottom: active?'2px solid var(--orange-500)':'2px solid transparent', cursor:'pointer', display:'flex', alignItems:'center', gap:8}}>
                    {t.l}
                    {t.n != null && <span style={{padding:'1px 8px', fontSize:10, fontWeight:700, borderRadius:99, background: active?'var(--orange-500)':'var(--bg-deep)', color: active?'#fff':'var(--ink-3)'}}>{t.n.toLocaleString()}</span>}
                  </div>
                );
              })}
              <div onClick={()=>{ setShowResolved(s=>!s); setTab(null); setSelectedId(null); }}
                style={{padding:'12px 18px', fontSize:13, fontWeight: showResolved?700:500, color: showResolved?'var(--orange-700)':'var(--ink-3)', borderBottom: showResolved?'2px solid var(--orange-500)':'2px solid transparent', cursor:'pointer'}}>
                완료 / 거부
              </div>
              <div style={{flex:1}}/>
            </div>

            <div style={{display:'grid', gridTemplateColumns:'1.4fr 1fr', gap:14}}>
              <div className="card" style={{overflow:'hidden'}}>
                <div style={{padding:'12px 16px', borderBottom:'1px solid var(--border-soft)', display:'flex', alignItems:'center', gap:10}}>
                  <div className="searchbar" style={{flex:1}}>
                    {Ic.search}
                    <input className="input" placeholder="제목·상세·신청자 검색"
                      value={q} onChange={e=>setQ(e.target.value)}/>
                  </div>
                  {q && (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={()=>setQ("")}
                      title="검색어 지우기">지우기</button>
                  )}
                </div>
                {visible.length === 0 ? (
                  <div className="muted" style={{padding:'40px', textAlign:'center'}}>해당 항목 없음</div>
                ) : (
                  <table className="tbl">
                    <thead><tr><th style={{paddingLeft:22}}>신청자</th><th>종류</th><th>제목</th><th>접수일</th><th>상태</th></tr></thead>
                    <tbody>
                    {visible.map((a, i) => (
                      <tr key={a.id} onClick={()=>setSelectedId(a.id)}
                        style={{background: selected?.id===a.id?'var(--orange-50)':'transparent', cursor:'pointer'}}>
                        <td style={{paddingLeft:22}}>
                          <div style={{display:'flex', alignItems:'center', gap:10}}>
                            <div className={"avatar sm " + (i%2?'green':'')}>{a.requester_name?.[0] || '?'}</div>
                            <b>{a.requester_name || '-'}</b>
                          </div>
                        </td>
                        <td><span className={"badge badge-" + KIND_COLOR[a.kind]}>{KIND_LABEL[a.kind]}</span></td>
                        <td><span className="muted">{a.title}</span></td>
                        <td className="mono muted">{new Date(a.created_at).toLocaleDateString('ko-KR')}</td>
                        <td>
                          {a.status === 'pending' && <span className="badge badge-amber">대기</span>}
                          {a.status === 'approved' && <span className="badge badge-green">승인</span>}
                          {a.status === 'rejected' && <span className="badge badge-rose">반려</span>}
                        </td>
                      </tr>
                    ))}
                    </tbody>
                  </table>
                )}
                <div style={{padding:'12px 22px', borderTop:'1px solid var(--border-soft)', display:'flex', alignItems:'center', justifyContent:'space-between', fontSize:11, color:'var(--ink-3)', gap:10}}>
                  <div>
                    전체 <b>{(data.pagination?.total || 0).toLocaleString()}</b>건 ·
                    페이지 <b>{data.pagination?.page || 1}</b> / {data.pagination?.total_pages || 1}
                  </div>
                  <Pager
                    page={data.pagination?.page || 1}
                    totalPages={data.pagination?.total_pages || 1}
                    onPage={setPage}
                  />
                </div>
              </div>

              <div className="card" style={{overflow:'hidden', position:'relative'}}>
                {selected ? (
                  <>
                    <div style={{padding:'22px 22px 14px', background:'linear-gradient(135deg, var(--orange-50), var(--bg-sub))', borderBottom:'1px solid var(--border-soft)'}}>
                      <div style={{display:'flex', alignItems:'center', gap:12}}>
                        <div className="avatar lg">{selected.requester_name?.[0] || '?'}</div>
                        <div style={{flex:1}}>
                          <div className="h2" style={{fontSize:18}}>{selected.title}</div>
                          <div className="muted" style={{fontSize:12}}>#{selected.id} · {selected.requester_name} ({selected.requester_role}) · {new Date(selected.created_at).toLocaleDateString('ko-KR')}</div>
                        </div>
                        <span className={"badge badge-" + KIND_COLOR[selected.kind]}>{KIND_LABEL[selected.kind]}</span>
                      </div>
                      {selected.status === 'pending' && perms.canApprove && (
                        <>
                          <div style={{display:'flex', gap:6, marginTop:14}}>
                            <button className="btn btn-primary btn-md" disabled={submitting}
                              onClick={()=>doApprove(selected)}>{Ic.check} {submitting ? '처리 중…' : '승인'}</button>
                            <button className="btn btn-ghost btn-md" disabled={submitting}
                              onClick={()=>doReject(selected)}>반려</button>
                          </div>
                          {actionErr && <div style={{marginTop:10, padding:'8px 12px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:8, fontSize:12}}>{actionErr}</div>}
                        </>
                      )}
                      {selected.status === 'pending' && !perms.canApprove && (
                        <div className="muted" style={{marginTop:14, padding:'8px 12px', background:'var(--bg-sub)', borderRadius:8, fontSize:12}}>
                          ⓘ 승인 처리는 권역 운영진만 가능합니다.
                        </div>
                      )}
                      {selected.status !== 'pending' && (
                        <div style={{marginTop:14, padding:'8px 12px', background:'#fff', borderRadius:8, fontSize:12.5}}>
                          <b>{selected.status === 'approved' ? '승인' : '반려'}</b> · {selected.reviewer_name} ({selected.reviewed_at ? new Date(selected.reviewed_at).toLocaleString('ko-KR') : ''})
                        </div>
                      )}
                    </div>

                    {/* 매니저 가입 심사: 신청자가 첨부한 자격증 증빙을 보고 검증 후 승인한다.
                        검토 시 가장 먼저 보는 자료이므로 액션 영역 바로 아래에 배치하고,
                        높이를 뷰포트에 맞게 제한해 긴 세로 사진도 화면에서 잘리지 않게 한다.
                        첨부가 PDF면 <img>로는 깨지므로 인라인 PDF 뷰어로, 이미지면 <img>로 분기 렌더. */}
                    {selected.has_license_image && (() => {
                      const url = `/api/v1/approvals/${selected.id}/license_image`;
                      const isPdf = selected.license_image_content_type === 'application/pdf';
                      return (
                        <div style={{padding:'18px 22px 0'}}>
                          <div className="muted" style={{fontSize:11, fontWeight:700, marginBottom:8, letterSpacing:'0.04em'}}>자격증 증빙</div>
                          {isPdf ? (
                            <iframe src={url} title="자격증 증빙(PDF)"
                                    style={{display:'block', width:'100%', height:'min(46vh, 400px)', border:'1px solid var(--border-soft)', borderRadius:8, background:'#fff'}}/>
                          ) : (
                            <a href={url} target="_blank" rel="noreferrer">
                              <img src={url} alt="자격증 사진"
                                   style={{display:'block', maxWidth:'100%', maxHeight:'min(46vh, 400px)', objectFit:'contain', borderRadius:8, border:'1px solid var(--border-soft)'}}/>
                            </a>
                          )}
                          <div className="muted" style={{fontSize:10, marginTop:4}}>
                            <a href={url} target="_blank" rel="noreferrer" style={{color:'var(--orange-700)'}}>새 탭에서 원본 보기 ↗</a>
                          </div>
                        </div>
                      );
                    })()}

                    <div style={{padding:'18px 22px'}}>
                      <div className="muted" style={{fontSize:11, fontWeight:700, marginBottom:8, letterSpacing:'0.04em'}}>상세 내용</div>
                      <div style={{padding:12, background:'var(--bg-sub)', borderRadius:8, fontSize:12.5, lineHeight:1.6}}>
                        {selected.detail || '추가 정보 없음'}
                      </div>
                    </div>

                    {selected.status === 'pending' && (
                      <div style={{padding:'14px 22px', background:'var(--bg-sub)', borderTop:'1px solid var(--border-soft)'}}>
                        <div className="muted" style={{fontSize:11, fontWeight:700, marginBottom:6}}>검토 메모 (감사로그)</div>
                        <textarea className="input" rows={3} value={reviewNote} onChange={e=>setReviewNote(e.target.value)} placeholder="승인/반려 사유를 적어주세요"/>
                      </div>
                    )}
                    {selected.review_note && (
                      <div style={{padding:'14px 22px', borderTop:'1px solid var(--border-soft)'}}>
                        <div className="muted" style={{fontSize:11, fontWeight:700, marginBottom:6}}>검토자 메모</div>
                        <div style={{fontSize:12.5}}>{selected.review_note}</div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="muted" style={{padding:30, textAlign:'center'}}>승인 요청을 선택하세요</div>
                )}
              </div>
            </div>

          </div>
          <AppFooter/>
        </div>
      </div>
    </div>
  );
}
export { Approval };
